import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import TasksForm from "../Tasks/Forms/TasksForm";
import AntdTable from "../antd/Table/Table";
import { popupcontext } from "../../context/Popupscontext";
import { useGetAllRequestsQuery } from "../../Redux/service/Requests/Request";
import { filtrationcontext } from "../../context/Filtercontext";
import { formatDateString } from "../../JsHelpers/DateFormat";
import RequestsHead from "./RequestsHead";
import Actions from "../antd/Table/Actions";
import RequestCancelPopup from "./RequestCancelPopup";
import { Tag } from "antd";
import BreadCrumb from "../Header/SecondHeader";
import FormRequest from "./FormRequest/FormRequest";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";

const Requests = () => {
  const [requestForm, setRequestForm] = useState(false);
  const [requestTask, setRequestTask] = useState(null); // <-- state for selected request
  const [refresh, setRefresh] = useState(false);
  const { handleShow, deleteItem, setDeleteItem, setEdited, edited } =
    useContext(popupcontext);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    userId: null,
    serviceType: null,
  });
  const { create, view, edit: canEdit, delete: canDelete } = usePermission("REQUESTS")
  const { create: canAddTask } = usePermission("TASKS")

  const buildQueryString = (filterObj) => {
    const query = Object.entries(filterObj)
      .filter(
        ([_, value]) => value !== null && value !== "" && value !== undefined
      )
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return query ? `?${query}` : "";
  };
  const statusColors = {
    inProgress: `blue`,
    pending: `orange`,
    completed: `green`,
    cancelled: `red`,
    none: `default`,
    Assigned: `purple`,
  };

  const queryString = buildQueryString(filter);
  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);
  const [sort, setSort] = useState({
    by: "",
    order: "",
  });
  const {
    data: RequestsData,
    isFetching,
    refetch,
  } = useGetAllRequestsQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      order: sort.order,
      queryString: queryString,
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    async function fetchdata() {
      if (edited) {
        await refetch();
        setEdited(false);
      }
      if (refresh) {
        await refetch();
        setRefresh(false);
      }
    }
    fetchdata();
  }, [edited, refresh]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem("page", currentPage);
  }, [currentPage]);
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }
  };
  useEffect(() => {
    if (deleteItem.condition === true && deleteItem.value === "request") {
      refetch();
      if (RequestsData?.data.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        localStorage.setItem("page", currentPage);
      }
      setDeleteItem({ condition: null, value: null });
    }
  }, [
    currentPage,
    deleteItem.condition,
    deleteItem.value,
    refetch,
    setDeleteItem,
    RequestsData?.data,
  ]);
  const [cancelRequest, setCancelRequest] = useState(null);
  const handleCancelRequest = async (id) => {
    setCancelRequest(id);
  };
  const columns = [
    {
      title: trans("Description"),
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      // render: (description) => (
      //   <Tooltip title={description}>
      //     <span className="text-span">{description}</span>
      //   </Tooltip>
      // ),
    },
    {
      title: trans("Service Type"),
      dataIndex: "serviceType",
      key: "serviceType",
      sorter: true,
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={statusColors[status] || statusColors.none}
          className="text-capitalize"
        >
          {trans(status)}
        </Tag>
      ),
    },
    {
      title: trans("Created At"),
      dataIndex: "createdAt",
      sorter: true,
      render: (createdAt) => <span>{formatDateString(createdAt)}</span>,
    },
    {
      title: trans("preferred time"),
      dataIndex: "preferredTime",
      key: "preferredTime",
      sorter: true,
      render: (preferredTime) => <span>{formatDateString(preferredTime)}</span>,
    },
    {
      title: trans("Actions"),
      width: 150,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) => (
        <Actions
          request={{ id: record.id, status: record.status, hasTasks: record.hasTasks }}
          type="request"
          setEditRequest={setRequestForm}
          handleCancelRequest={handleCancelRequest}
          onClick={() => {
            setRequestTask({
              id: record.id,
              name: record.serviceType,
              notes: record.description,
              startTime: record.preferredTime,
            });
            handleShow("addForm");
          }}
          canEdit={canEdit}
          canDelete={canDelete}
          canAddTask={canAddTask}
        />
      ),
    },
  ];
  if(!view){
    return <AccessDenied />;
  }
  return (
    <>
      <Helmet>
        <title>Incomming Requests</title>
        <meta name="description" content="Requests" />
      </Helmet>
      <BreadCrumb
        add={create ? trans("Add new request") : null}
        onClick={() => setRequestForm(true)}
        secButton={trans("Refresh")}
        secClick={() => setRefresh(true)}
        secLoading={isFetching}
      />
      <RequestsHead setFilter={setFilter} />
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={columns}
          loading={isFetching}
          dataSource={RequestsData?.data || []}
          pageSize={limit}
          totalItems={RequestsData?.total || 0} // Total count for pagination
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
          scrollX={true}
        />
      </div>

      <TasksForm requestTask={requestTask} />
      <FormRequest
        requestForm={requestForm}
        setRequestForm={setRequestForm}
        isEditing={typeof requestForm === "number"}
      />
      <RequestCancelPopup
        reqId={cancelRequest}
        refetch={refetch}
        setCancelRequest={setCancelRequest}
      />
    </>
  );
};

export default Requests;
